// validation.js — shared block-editor field validation (EW-014 / EW-015).
// WL-011 (Fable Hail Mary Wave 2).
//
// The audit's EW-014/EW-015 findings: block editors accepted invalid input
// silently (over-long titles, malformed hyperlinks, out-of-range numbers) and
// persisted it, producing broken saved wheels. This module is the ONE place
// those rules live so every editor validates the same way (no per-block drift).
//
// Each validator returns null when valid, or a human-readable error string.
// `validateField(value, rules)` runs a rule list and returns the first error.

export const required = (msg = "This field is required") => (v) =>
  v == null || String(v).trim() === "" ? msg : null;

export const maxLength = (n, msg) => (v) =>
  v != null && String(v).length > n
    ? msg || `Keep it under ${n} characters (${String(v).length}/${n}).`
    : null;

export const minLength = (n, msg) => (v) =>
  v != null && String(v).trim() !== "" && String(v).length < n
    ? msg || `Use at least ${n} characters.`
    : null;

// EW-015: hyperlink fields silently accepted junk. Accept http(s), mailto, tel,
// and protocol-relative — reject bare junk. Empty is allowed (use `required`).
const URL_RE = /^(https?:\/\/|mailto:|tel:|\/\/)[^\s]+$/i;
export const url = (msg = "Enter a full link (https://…) or leave it empty") => (v) => {
  if (v == null || String(v).trim() === "") return null;
  const s = String(v).trim();
  // Allow a scheme-less domain like "example.com" but flag spaces / obvious junk.
  if (URL_RE.test(s)) return null;
  if (/^[\w-]+(\.[\w-]+)+([/?#].*)?$/i.test(s)) return null; // domain.tld/path
  return msg;
};

export const numberInRange = (min, max, msg) => (v) => {
  const n = Number(v);
  if (v === "" || v == null || Number.isNaN(n))
    return msg || "Enter a number.";
  if (n < min || n > max) return msg || `Enter a value between ${min} and ${max}.`;
  return null;
};

// Run a list of validators; return the first error message or null.
export const validateField = (value, rules = []) => {
  for (const rule of rules) {
    const err = rule(value);
    if (err) return err;
  }
  return null;
};

// Clamp a numeric value into [min,max] — for steppers/sliders that must never
// persist an out-of-range value (defence in depth alongside numberInRange).
export const clamp = (n, min, max) => Math.min(max, Math.max(min, Number(n)));

export default { required, maxLength, minLength, url, numberInRange, validateField, clamp };
